import { resourceListBindings } from 'tmr/components/resourceList/resourceListHelper'
import templateUrl from './printQueue.html'

/**
 * Print.
 *
 * @namespace routes.OperationPrintQueue
 * @type {TmrRoute}
 */
export const route = {
  name: 'operations.print.queue',
  label: 'OPERATIONS.PRINT.QUEUE.TITLE',
  url: '/print/queue',
  params: {
    completed: {
      value: 'false',
      squash: true,
    },
    sessionsToCancel: {
      value: [],
      dynamic: true,
    },
  },
  resolve: {
    printers: /*@ngInject*/ Printer => Printer.query(),
    printSessions: /*@ngInject*/ ($injector, resources) =>
      resources.map(resource =>
        // ProductPrintSessionDto type > ProductPrintSession TmrResource type service
        $injector.get(resource.type.replace('Dto', '')).create(resource)
      ),
  },
  resourceType: 'PrintSession',
  resourceList: {
    searchParameters: ['completed', 'printer.id'],
  },
  component: 'tmrOperationPrintQueue',
  accessControl: ['Operation.perform', 'Operation.PrintQueue.perform'],
  mainMenu: false,
  weight: 999,
}

/**
 * Print component.
 *
 * @type {ngComponent}
 */
export const tmrOperationPrintQueue = {
  ngType: 'component',
  templateUrl,
  controller: 'tmrOperationPrintQueueController',
  bindings: resourceListBindings({
    printers: '<',
    printSessions: '<',
  }),
}

/**
 * Controller of component {@link tmrOperationPrintQueue}.
 *
 * @type {ngController}
 */
export class tmrOperationPrintQueueController {
  static ngType = 'controller'

  constructor(
    $state,
    $stateParams,
    TmrPaging,
    PrintSession,
    TmrPromptModal,
    TmrToast
  ) {
    'ngInject'
    Object.assign(this, {
      $state,
      $stateParams,
      TmrPaging,
      PrintSession,
      TmrPromptModal,
      TmrToast,
    })
  }

  $onInit() {
    if (this.$stateParams['printer.id']) {
      this.printers.$promise.then(() => {
        this.printer = this.printers.find(
          ({ id }) => id == this.$stateParams['printer.id']
        )
        return this.printer ?? this.onPrinterChange()
      })
    }
    this.sessionsToCancel = this.$stateParams.sessionsToCancel
    this.printSessions.forEach(
      printSession =>
        (printSession.$toDelete =
          this.sessionsToCancel.find(id => id === printSession.id) !==
          undefined)
    )
  }

  onPrinterChange() {
    return this.$state.go('.', {
      'printer.id': this.printer ? this.printer.id : null,
    })
  }

  goToPage(page) {
    return this.TmrPaging.go(page)
  }

  askCancelPrintSession($event, printSession) {
    this.TmrPromptModal($event, {
      question: 'OPERATIONS.PRINT.QUEUE.DELETECONFIRM',
      yesCallback: () => this.cancelPrintSession(printSession.id),
      relativeTo: `.print-session-delete-${printSession.id}`,
    })
  }

  cancelPrintSession(sessionId) {
    this.sessionsToCancel.length = 0
    return this.PrintSession.cancel({ id: sessionId })
      .$promise.then(() =>
        this.$state.go(this.$state.current, null, {
          reload: true,
          inherit: false,
        })
      )
      .then(() => this.TmrToast.success('OPERATIONS.PRINT.QUEUE.DELETEDMSG'))
      .catch(err => this.TmrToast.exception(err))
  }

  isDeletable({ status, printCounters }) {
    return (
      !['COMPLETED', 'CANCELLED'].includes(status) &&
      printCounters.printed < printCounters.requested
    )
  }

  get deletablePrintSessions() {
    return this.printSessions.filter(printsession =>
      this.isDeletable(printsession)
    )
  }

  isToggleAllChecked() {
    return this.printSessions.every(({ $toDelete }) => $toDelete)
  }

  isToggleAllIndeterminate() {
    return (
      this.printSessions.some(res => res.$toDelete) &&
      !this.printSessions.every(res => res.$toDelete)
    )
  }

  toggleToDelete(printSession) {
    let isToDelete = this.sessionsToCancel.includes(printSession.id)
    if (printSession.$toDelete && !isToDelete)
      return this.addResourcesToDelete([printSession])
    else if (!printSession.$toDelete && isToDelete)
      return this.removeResourcesToDelete([printSession])
  }

  toggleToDeleteAll() {
    let allChecked = this.isToggleAllChecked()
    let toToggle = []
    this.deletablePrintSessions.forEach(resource => {
      // XNOR.
      if (allChecked == this.sessionsToCancel.includes(resource.id))
        toToggle.push(resource)
    })
    if (toToggle.length) {
      toToggle.forEach(resource => (resource.$toDelete = !allChecked))
      if (allChecked) return this.removeResourcesToDelete(toToggle)
      else return this.addResourcesToDelete(toToggle)
    }
  }

  addResourcesToDelete(list) {
    list.forEach(l => this.sessionsToCancel.push(l.id))
    this.$state.go('.', { sessionsToCancel: this.sessionsToCancel })
  }

  removeResourcesToDelete(list) {
    list.forEach(l =>
      this.sessionsToCancel.splice(this.sessionsToCancel.indexOf(l.id), 1)
    )
    this.$state.go('.', { sessionsToCancel: this.sessionsToCancel })
  }

  askCancelMultiPrintSession($event) {
    this.TmrPromptModal($event, {
      question: 'OPERATIONS.PRINT.QUEUE.MULTIDELETECONFIRM',
      yesCallback: () => this.cancelMultiPrintSession(),
    })
  }

  cancelMultiPrintSession() {
    let sessionsToCancel = [].concat(this.sessionsToCancel)
    this.sessionsToCancel.length = 0
    return this.PrintSession.bulkCancel(sessionsToCancel)
      .$promise.then(() =>
        this.$state.go(
          this.$state.current,
          { 'printer.id': this.printer ? this.printer.id : null },
          { reload: true, inherit: false }
        )
      )
      .then(() =>
        this.TmrToast.success('OPERATIONS.PRINT.QUEUE.MULTIDELETEDMSG')
      )
      .catch(err => this.TmrToast.exception(err))
  }
}
